import * as React from 'react'
import { fromJSX } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { DP } from '../../../drupal/blockDefinitions'
import { quoteSrc, quoteSrcDesktop, quoteSrcSet, quoteSrcSetDesktop } from '../../../drupal/utils/imageSizes'
import { parseDrupalImageUrls } from '../../../drupal/utils/modernImage'
import { dummyData, randomWords } from '../../../utils/dummyData'
import { linkWrapper } from '../../../utils/link'
import { ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { quoteBlockClasses, QuoteBlockLayout } from './quoteBlock.layout'
import { QuoteBlockProps } from './quoteBlock.state'

export const quoteBlock: ModernBlock<QuoteBlockProps, any, any, DP.Quote | DP.QuoteImage> = {
  kind: 'quoteBlock',
  render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
    fromJSX((updateBlock) => (
      <QuoteBlockLayout.Inner
        amountNested={amountNested!}
        hasImage={blockProps.image ? true : false}
        design={blockProps.design}
      >
        {blockProps.design === 'default' ? (
          <QuoteBlockLayout.DefaultContent
            hasImage={blockProps.image ? true : false}
            text={blockProps.text}
            author={blockProps.author}
            authorDescription={blockProps.authorDescription}
            showPrimary={blockProps.showPrimary}
            showSecondary={blockProps.showSecondary}
            primaryAction={blockProps.primaryAction!}
            secondaryAction={blockProps.secondaryAction!}
            showActionAsLink={blockProps.showActionAsLink}
          />
        ) : (
          <QuoteBlockLayout.LayoutNewDesign
            hasImage={blockProps.image ? true : false}
            text={blockProps.text}
            author={blockProps.author}
            authorDescription={blockProps.authorDescription}
            blockIndex={`${blockIndex}`}
            image={blockProps.image}
            imageSources={blockProps.imageSources}
            primaryAction={blockProps.primaryAction}
            showPrimary={blockProps.showPrimary}
            showActionAsLink={blockProps.showActionAsLink}
          ></QuoteBlockLayout.LayoutNewDesign>
        )}
        {blockProps.design === "default" && <QuoteBlockLayout.DefaultVisual
          blockIndex={`${blockIndex}`}
          hasImage={blockProps.image && blockProps.imageSources ? true : false}
          image={blockProps.image}
          imageSources={blockProps.imageSources}
        />}
      </QuoteBlockLayout.Inner>
    )),
  blockClassName: (props) => `
    ${getClassNameModern(quoteBlockClasses, 'block', 'mw-container-s mx-auto px-gutter mb-6')}
    ${getClassNameModern(quoteBlockClasses, props.image ? 'blockImage' : 'blockText', props.image ? '' : 'text-center')}
  `,
  inView: {
    showAs: 'figure',
    threshold: 0.1,
    triggerOnce: true,
    inViewClassName: getClassNameModern(quoteBlockClasses, 'inview', 'inview'),
    updateState: (currentInviewState, currentBlockState) => {
      return {
        ...currentBlockState,
        inviewState: currentInviewState,
      }
    },
  },
  adapters: {
    drupal: (dpBlock: any, blockIndex: number) => {
      const targetId = dpBlock.target.type[0].target_id
      if (targetId != 'quote' && targetId != 'quote_with_image' && targetId != 'quote_nested') return undefined
      const fieldMediaImage = dpBlock.target.field_image[0]?.target.field_media_image[0]
      const fieldMediaSource = dpBlock.target.field_image[0]?.target.field_source[0]
        ? dpBlock.target.field_image[0]?.target.field_source
        : undefined
      const dpImages =
        targetId == 'quote_with_image'
          ? parseDrupalImageUrls(fieldMediaImage, fieldMediaSource, quoteSrcSet, quoteSrc)?.image
          : undefined
      const dpImagesDesktop =
        targetId == 'quote_with_image'
          ? parseDrupalImageUrls(fieldMediaImage, fieldMediaSource, quoteSrcSetDesktop, quoteSrcDesktop)?.image
          : undefined
      return {
        design: dpBlock.target?.field_design && dpBlock.target?.field_design[0].value == 'new' ? 'new' : 'default',
        text: dpBlock.target?.field_text[0]?.value,
        author: dpBlock.target.field_author_name ? dpBlock.target.field_author_name[0]?.value : undefined,
        authorDescription: dpBlock.target.field_author_description
          ? dpBlock.target.field_author_description[0]?.value
          : undefined,
        image: targetId == 'quote' ? undefined : dpImages,
        imageSources:
          targetId == 'quote'
            ? undefined
            : dpImagesDesktop
            ? [
                {
                  imageProportions: {
                    width: '475',
                    height: '356',
                    sizes: '(min-width: 1024px) 475px, 100vw',
                    media: '(min-width: 1024px)',
                  },
                  image: dpImagesDesktop,
                },
              ]
            : [],
        anchorTitle: `quote-${blockIndex}`,
        showPrimary: dpBlock.target?.field_links[0] ? true : false,
        showSecondary: dpBlock.target?.field_links[1] ? true : false,
        primaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.options?.attributes?.id! : '',
        }),
        secondaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.options?.attributes?.id! : '',
        }),
        showActionAsLink: dpBlock.target.field_link_type[0]?.value == true ? true : false,
      }
    },
  },
  seoEvents: (props, blockIndex) =>
    [
      props.primaryAction
        ? {
            id: 'primary_button',
            event: 'onClick',
            category: 'quote_block',
            label: window.location.pathname,
            action: 'primary_' + props.primaryAction.title,
          }
        : undefined,
    ].filter((i) => i != undefined) as SeoEvent[],
  sampleExample: [
    {
      design: 'default',
      text: dummyData.quote,
      anchorTitle: 'quote-sample-1',
      author: dummyData.person,
      showPrimary: true,
      showSecondary: true,
      primaryAction: dummyData.primaryButton,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
    },
    {
      design: 'default',
      text: dummyData.quote,
      anchorTitle: 'quote-sample-2',
      author: dummyData.person,
      authorDescription: randomWords(),
      image: dummyData.image,
      imageSources: dummyData.imageSources,
      showPrimary: true,
      showSecondary: true,
      primaryAction: dummyData.primaryButton,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
    },
    {
      design: 'new',
      text: dummyData.quote,
      anchorTitle: 'quote-sample-3',
      author: dummyData.person,
      authorDescription: randomWords(),
      showPrimary: true,
      showSecondary: true,
      primaryAction: dummyData.primaryButton,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
    },
    {
      design: 'new',
      text: dummyData.quote,
      anchorTitle: 'quote-sample-5',
      author: dummyData.person,
      authorDescription: randomWords(),
      image: dummyData.image,
      imageSources: dummyData.imageSources,
      showPrimary: true,
      showSecondary: true,
      primaryAction: dummyData.primaryButton,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
    },
  ],
}
